import React, { useEffect } from 'react';
import './tariff.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import PageHeader from '../../../components/base/PageHeader/PageHeader';
import {
  Button, Col, Row,
} from '../../../components/base/BootstrapComponents';
import { getTariffData } from '../reducer';
import notificationDecorator from '../../Notifications/notificationDecorator';
import { responseLogWithNotification } from '../../../utils/utils';
import {
  createLostTicket,
  createTariff,
  editLostTicket,
  editTariff,
} from '../actions';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import TariffDTO from '../../../dto/TariffDTO';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import Loader from '../../../components/base/Loader/Loader';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import TimePicker from '../../../components/fields/TimePicker/TimePicker';
import Switches from '../../../components/fields/Switches/Switches';
import PricingStepsEditor, {
  createEmptyPricingStep,
  normalizePricingPayload,
  normalizePricingStepsForForm,
} from './PricingStepsEditor';

const normalizeLostTicketCost = value => {
  if (typeof value === 'number') {
    return value;
  }

  const rawValue = String(value || '').replace(',', '.').trim();
  if (!rawValue) {
    return 0;
  }

  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function TariffForm(props) {
  const {
    handleClose = () => {}, handleEdit = () => {}, handleDelete = null, loadData = () => {}, hasView = false,
  } = props;
  const dispatch = useDispatch();
  const {
    currentTariff,
    loadingIntervalList,
    lostTicketList,
    loadingCreateLostTicket,
    loadingEditLostTicket,
  } = useSelector(getTariffData);
  const lostTicket = Array.isArray(lostTicketList) && lostTicketList.length ? lostTicketList[0] : null;
  const defaultValues = currentTariff?.id
    ? {
      ...currentTariff,
      type: 'single',
      pricing_mode: currentTariff?.pricing_mode || 'flat',
      pricing_steps: normalizePricingStepsForForm(currentTariff?.pricing_steps),
    }
    : {
      is_active: true,
      type: 'single',
      pricing_mode: 'flat',
      pricing_steps: [],
    };
  const {
    control, getValues, watch, setValue,
  } = useForm({
    defaultValues,
  });
  const pricingMode = watch('pricing_mode') || 'flat';
  const isLostTicketMode = pricingMode === 'lost_ticket';
  const pricingSteps = watch('pricing_steps') || [];
  const timeUnit = watch('time_unit') || currentTariff?.time_unit || 'hour';

  useEffect(() => {
    if (pricingMode === 'lost_ticket' && !currentTariff?.id && !getValues('name')) {
      setValue('name', lostTicket?.name || 'Штрафной билет');
    }
    if (pricingMode === 'tiered' && !pricingSteps.length) {
      setValue('pricing_steps', [createEmptyPricingStep()]);
    }
    if (pricingMode !== 'tiered' && pricingSteps.length) {
      setValue('pricing_steps', []);
    }
  }, [pricingMode, pricingSteps.length, setValue, currentTariff?.id, getValues, lostTicket?.name]);

  const handleSave = () => {
    const isUpdate = currentTariff?.id;
    const data = getValues();

    if (data.pricing_mode === 'lost_ticket') {
      const cost = normalizeLostTicketCost(data.cost);
      if (cost === null) {
        notificationDecorator({
          title: 'Штрафной билет',
          message: 'Укажите корректную стоимость',
          isError: true,
        });
        return;
      }

      const action = lostTicket?.id ? editLostTicket : createLostTicket;
      const payload = {
        id: lostTicket?.id,
        name: String(data?.name || 'Штрафной билет').trim() || 'Штрафной билет',
        description: data?.description || null,
        cost,
        is_active: Boolean(data?.is_active),
      };

      dispatch(action(payload))
        .then(() => {
          loadData();
          handleClose();
          notificationDecorator({
            title: lostTicket?.id ? 'Редактирование штрафного билета' : 'Создание штрафного билета',
            message: lostTicket?.id ? 'Редактирование штрафного билета прошло успешно' : 'Создание штрафного билета прошло успешно',
          });
        })
        .catch(responseLogWithNotification);
      return;
    }

    const action = isUpdate ? editTariff : createTariff;
    const normalizedSteps = normalizePricingPayload(data.pricing_mode, data.pricing_steps);
    const payload = {
      ...data,
      type: 'single',
      pricing_mode: data.pricing_mode || 'flat',
    };

    if (payload.pricing_mode === 'tiered') {
      payload.pricing_steps = normalizedSteps;
      payload.cost = normalizedSteps?.[0]?.cost ?? 0;
    } else {
      delete payload.pricing_steps;
    }

    dispatch(action(payload))
      .then(() => {
        loadData();
        handleClose();
        notificationDecorator({
          title: isUpdate ? 'Редактирование тарифа' : 'Создание тарифа',
          message: isUpdate ? 'Редактирование тарифа прошло успешно' : 'Создание тарифа прошло успешно',
        });
      })
      .catch(responseLogWithNotification);
  };

  const tariffDTO = new TariffDTO();
  const attributes = tariffDTO.getAttributesByName();

  const vehicleTypeOptions = [
    { nameId: 'vehicle.type.car', value: 'car' },
    { nameId: 'vehicle.type.bus', value: 'bus' },
    { nameId: 'vehicle.type.motorcycle', value: 'motorcycle' },
    { nameId: 'vehicle.type.truck', value: 'truck' },
    { nameId: 'vehicle.type.all', value: 'all' },
  ];

  const tariffDateTypeOptions = [
    { nameId: 'tariff.date.type.workday', value: 'workday' },
    { nameId: 'tariff.date.type.saturday', value: 'saturday' },
    { nameId: 'tariff.date.type.sunday', value: 'sunday' },
    { nameId: 'tariff.date.type.holiday', value: 'holiday' },
  ];

  const timeUnitOptions = [
    { nameId: 'time.unit.minute', value: 'minute' },
    { nameId: 'time.unit.hour', value: 'hour' },
    { nameId: 'time.unit.day', value: 'day' },
  ];

  const pricingModeOptions = [
    { nameId: 'pricing.mode.flat', value: 'flat' },
    { nameId: 'pricing.mode.tiered', value: 'tiered' },
    ...(!currentTariff?.id ? [{ nameId: 'pricing.mode.lost_ticket', value: 'lost_ticket' }] : []),
  ];

  const formHeader = isLostTicketMode
    ? (lostTicket?.id ? 'Редактирование штрафного билета' : 'Создание штрафного билета')
    : currentTariff?.id ? 'Редактирование тарифа' : 'Создание тарифа';

  const dataHeader = isLostTicketMode ? 'Данные штрафного билета' : 'Данные тарифа';

  return (
    <div className="page-container tariff-form">
      <Loader loading={loadingIntervalList || loadingCreateLostTicket || loadingEditLostTicket} />
      <div className="inputs-box">
        <PageHeader header={formHeader} />
      </div>
      <div className="plp-table g-0">
        <div className="frame py-3 d-flex flex-column px-3">
          <div className="d-flex flex-row justify-content-between align-items-center">
            <div className="h4">
              {dataHeader}
            </div>
          </div>
          <Row className="h-100">
            <Col columns={6}>
              <FieldDTO
                control={control}
                type="text"
                className="mb-2"
                dtoObject={attributes.name}
                addPlaceholder
                component={TextInputWithLabel}
                disabled={hasView}
              />
              <FieldDTO
                control={control}
                type="text"
                className="mb-2"
                dtoObject={{
                  name: 'pricing_mode',
                  type: String,
                  validators: {},
                  labelKey: 'pricing.mode',
                }}
                addPlaceholder
                component={CustomSelectInput}
                disabled={hasView}
                options={pricingModeOptions}
                withLabel
              />
              {!isLostTicketMode
                ? (
                  <>
                    <Row>
                      <Col columns={6}>
                        <FieldDTO
                          control={control}
                          type="text"
                          className="mb-2"
                          dtoObject={attributes.start_time}
                          addPlaceholder
                          component={TimePicker}
                          disabled={hasView}
                        />
                      </Col>
                      <Col columns={6}>
                        <FieldDTO
                          control={control}
                          type="text"
                          className="mb-2"
                          dtoObject={attributes.end_time}
                          addPlaceholder
                          component={TimePicker}
                          disabled={hasView}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col columns={6}>
                        <FieldDTO
                          control={control}
                          type="text"
                          className="mb-2"
                          dtoObject={attributes.vehicle_type}
                          addPlaceholder
                          component={CustomSelectInput}
                          disabled={hasView}
                          options={vehicleTypeOptions}
                          withLabel
                        />
                      </Col>
                      <Col columns={6}>
                        <FieldDTO
                          control={control}
                          type="text"
                          className="mb-2"
                          dtoObject={attributes.time_unit}
                          addPlaceholder
                          component={CustomSelectInput}
                          disabled={hasView}
                          options={timeUnitOptions}
                          withLabel
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col columns={6}>
                        <FieldDTO
                          control={control}
                          type="number"
                          className="mb-2"
                          dtoObject={attributes.free_duration}
                          addPlaceholder
                          component={TextInputWithLabel}
                          disabled={hasView}
                        />
                      </Col>
                      <Col columns={6}>
                        <FieldDTO
                          control={control}
                          type="text"
                          className="mb-2"
                          dtoObject={attributes.day_types}
                          addPlaceholder
                          component={CustomSelectInput}
                          options={tariffDateTypeOptions}
                          disabled={hasView}
                          withLabel
                          multiple
                        />
                      </Col>
                    </Row>
                  </>
                )
                : null}
              <Row>
                {pricingMode !== 'tiered'
                  ? (
                    <Col columns={12}>
                      <FieldDTO
                        control={control}
                        type={isLostTicketMode ? 'text' : 'number'}
                        className="mb-2"
                        dtoObject={attributes.cost}
                        addPlaceholder
                        component={TextInputWithLabel}
                        disabled={hasView}
                      />
                    </Col>
                  )
                  : null}
              </Row>
              {pricingMode === 'tiered'
                ? (
                  <PricingStepsEditor
                    control={control}
                    pricingSteps={pricingSteps}
                    setPricingSteps={steps => setValue('pricing_steps', steps)}
                    disabled={hasView}
                    timeUnit={timeUnit}
                  />
                )
                : null}
              <FieldDTO
                control={control}
                type="text"
                className="mb-2"
                dtoObject={attributes.description}
                addPlaceholder
                component={TextInputWithLabel}
                disabled={hasView}
                textarea
              />
              <FieldDTO
                control={control}
                type="text"
                className="mb-2"
                dtoObject={attributes.is_active}
                addPlaceholder
                component={Switches}
                disabled={hasView}
                textarea
              />
              <div className="text-center justify-content-center p-3">
                {!hasView
                  ? <Button className="me-3 px-3" onClick={handleSave}>Сохранить</Button>
                  : <Button className="me-3 px-3" onClick={handleEdit(currentTariff)}>Редактировать</Button>}

                {currentTariff?.id && !isLostTicketMode && handleDelete
                  ? <Button variant="danger" className="me-3 px-3" onClick={handleDelete(currentTariff)}>Удалить</Button>
                  : null}

                <Button variant="secondary" className="px-3" onClick={handleClose}>Закрыть</Button>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}
